import { useCallback, useEffect, useState } from 'react';
import { useKeystoneSession } from '../auth/use-keystone-session';
import { useToast } from '@chakra-ui/react';
import type { AnnotationPreset } from '@rta/rta-blueprint-component';

import { deleteAnnotationPreset, getUserPreferences, saveAnnotationPreset } from '@external';

export type UseAnnotationPresetsResult = {
	annotationPresets: Array<AnnotationPreset>;
	onSaveAnnotationPreset: (name: string, annotations: Array<Record<string, unknown>>) => Promise<void>;
	onDeleteAnnotationPreset: (presetId: string) => Promise<void>;
};

// One id so a remount (design/customer switch) re-toasts the same load failure instead of stacking a new one
const LOAD_ERROR_TOAST_ID = 'annotation-presets-load-error';

// #354 -- feeds RtaBlueprintComponent's annotationPresets/onSave.../onDelete... props. Scoped to the caller's own Keystone session, not the open customer/design's CRM contact -- the API derives identity from the JWT itself, so this is stable across switching design/customer
export const useAnnotationPresets = (): UseAnnotationPresetsResult => {
	const { getToken, status } = useKeystoneSession();
	const [annotationPresets, setAnnotationPresets] = useState<Array<AnnotationPreset>>([]);
	const toast = useToast();

	const refresh = useCallback(async (): Promise<void> => {
		const [data, error] = await getUserPreferences({ getToken });
		if (error) {
			if (!toast.isActive(LOAD_ERROR_TOAST_ID)) toast({ id: LOAD_ERROR_TOAST_ID, title: 'Could not load your saved presets', description: error, status: 'error', isClosable: true });
			return;
		}
		setAnnotationPresets(data?.annotationPresets ?? []);
	}, [getToken, toast]);

	// getToken rejects while the Keystone session is still resolving, so the first fetch has to wait for readiness -- without this gate a single unlucky mount leaves the panel empty for the whole session
	useEffect(() => {
		if (status === 'ready') refresh();
	}, [status, refresh]);

	const onSaveAnnotationPreset = useCallback(async (name: string, annotations: Array<Record<string, unknown>>): Promise<void> => {
		const [data, error] = await saveAnnotationPreset({ name, annotations, getToken });
		if (error) {
			toast({ title: 'Could not save preset', description: error, status: 'error', isClosable: true });
			return;
		}
		setAnnotationPresets(data?.annotationPresets ?? []);
		toast({ title: `Saved preset "${name}"`, status: 'success', duration: 2500, isClosable: true });
	}, [getToken, toast]);

	const onDeleteAnnotationPreset = useCallback(async (presetId: string): Promise<void> => {
		const [data, error] = await deleteAnnotationPreset({ presetId, getToken });
		if (error) {
			toast({ title: 'Could not delete preset', description: error, status: 'error', isClosable: true });
			return;
		}
		setAnnotationPresets(data?.annotationPresets ?? []);
	}, [getToken, toast]);

	return { annotationPresets, onSaveAnnotationPreset, onDeleteAnnotationPreset };
};
